import React, { Fragment } from 'react'
import { useSelector } from 'react-redux'
import { injectIntl, InjectedIntlProps } from 'react-intl'
import cx from 'classnames'
import TitledContent from 'components/TitledContent/TitledContent'
import Tooltip from 'components/Tooltip/Tooltip'
import Income from 'components/Income/Income'

import { formattedDate } from 'utils/formatters'

import NoData from 'components/NoData/NoData'
import { selectCurrentBusiness } from 'store/business'
import BusinessZoneCount from 'components/BusinessZoneCount/BusinessZoneCount'
import messages from './messages'
import styles from './BusinessSummary.scss'

const sections = {
  details: ['businessType', 'incorpDate', 'firstActivityDate'],
  count: ['employees', 'location'],
  income: ['reported', 'estimated', 'documentedIncome'],
} as const

interface BusinessSummaryProps extends InjectedIntlProps {
  isSpaced?: boolean
}

function BusinessSummary({ intl, isSpaced = true }: BusinessSummaryProps) {
  const currentBusiness = useSelector(selectCurrentBusiness)

  const renderContent = (field: string) => {
    if (field === 'location') {
      return <BusinessZoneCount key={field} type='location' counts={currentBusiness?.listings} />
    }
    if (field === 'employees') {
      return <BusinessZoneCount key={field} type='employee' counts={currentBusiness?.employees} />
    }

    if (field === 'firstActivityDate')
      return (
        <span>
          {currentBusiness?.firstActivityDate ? (
            formattedDate(currentBusiness.firstActivityDate, 'MMM DD, YYYY')
          ) : (
            <NoData />
          )}
        </span>
      )

    if (field === 'incorpDate')
      return (
        <span>
          {currentBusiness?.incorpDate ? formattedDate(currentBusiness.incorpDate, 'MMM DD, YYYY') : <NoData />}
        </span>
      )

    if (field === 'documentedIncome') {
      if (currentBusiness?.documentedIncome === -1) return <span>Out of State</span>
      return <Income value={currentBusiness?.documentedIncome} />
    }

    const value = currentBusiness?.[field as keyof typeof currentBusiness]
    return value ? <Tooltip title={String(value)}>{String(value)}</Tooltip> : <NoData />
  }

  return (
    <div className={styles.container}>
      <section className={styles.section}>
        {Object.keys(sections).map(key => (
          <Fragment key={key}>
            {sections[key as keyof typeof sections].map(field => (
              <TitledContent
                key={`${key}|${field}`}
                title={intl.formatMessage((messages.fields as any)[field])}
                className={cx(styles.field, styles[key])}
                titleClassName={styles.fieldTitle}
                contentClassName={cx(styles.fieldContent, styles[field])}
              >
                {renderContent(field)}
              </TitledContent>
            ))}
            {key !== 'income' && <span className={styles.separator} />}
          </Fragment>
        ))}
      </section>
    </div>
  )
}

export default injectIntl(BusinessSummary)

